import React, { useState } from 'react';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import { Currency } from '@strava/units/react';

import SubscriptionFeatures from '../shared/SubscriptionFeatures';
import DurationSelectSideBySide from '../Summary/DurationSelectSideBySide';
import Card from '../shared/Card';
import Inset from '../shared/Inset';
import PromoCodeInput from '../Summary/PricingList/PromoCodeInput';

import { useCheckoutContext } from '../../state/CheckoutContext';
import { I18nPrefixTrial, I18nPrefixFatmap } from '../../shared/constants';

import styles from './SummaryTrials.scss';

export const calculateTrialEndDate = (trialLength, startDate) => {
  const trialEndDate = startDate ? new Date(startDate) : new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialLength);
  return Cldr.formatDateTime(trialEndDate, {
    format: 'additional',
    type: 'yMMMd'
  });
};

const renderPerMonthPrice = (price, currency) =>
  I18n.t_html(`${I18nPrefixTrial}.per_month`, {
    price: Cldr.formatCurrency(price, currency)
  });

const renderPerYearPrice = (price, currency) =>
  I18n.t_html(`${I18nPrefixTrial}.per_year`, {
    price: Cldr.formatCurrency(price, currency)
  });

export const SummaryTrials = () => {
  const { checkoutEntityState } = useCheckoutContext();
  const { experiments, trialInfo, pricing, analyticsData } = checkoutEntityState;
  const {
    currency,
    billedToday,
    yearlyTotal,
    monthlyTotal,
    duration
  } = pricing;
  const { shouldShowTrustLanguage } = experiments;

  const [shouldShowBillingOptions, setShouldShowBillingOptions] = useState(false);
  const [shouldShowPromoCodeInput, setShouldShowPromoCodeInput] = useState(false);

  const shouldShowBillingOptionsBtn = !shouldShowBillingOptions;
  const shouldShowPromoCodeInputBtn = !shouldShowPromoCodeInput;

  const handleBillingOptionsClick = () => setShouldShowBillingOptions(true)
  const handlePromoClick = () => setShouldShowPromoCodeInput(true)

  const isYearlyDuration = duration === 'yearly';
  const totalPrimaryPrice = isYearlyDuration ? yearlyTotal : monthlyTotal;
  const isFromFatmap = analyticsData.origin === 'fatmap';

  const title = () => {
    if (isFromFatmap) {
      return I18n.t(`${I18nPrefixFatmap}.title_v2`)
    }
    return I18n.t(
      `${I18nPrefixTrial}.title_v2`,
      {
        days: trialInfo?.trialLength
      }
    )
  }
  const renderOverview = () => {
    if (shouldShowTrustLanguage && trialInfo?.trialLength) {
      return (
        <span>
          {I18n.t_html(`${I18nPrefixTrial}.disclaimer`, {
            trialEndDate: calculateTrialEndDate(trialInfo.trialLength)
          })}
        </span>
      );
    }
    return <SubscriptionFeatures />;
  };

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const moreBillingOptionsButtonText = I18n.t(
    `${I18nPrefixTrial}.more_billing_options`
  );

  /* Show divider (dot) between "More Billing Options" and "Redeem Promo Code"
     only if both of these hyperlink buttons are still visible
     (they become invisible when clicked on) */
  const shouldShowDivider = shouldShowBillingOptionsBtn && shouldShowPromoCodeInputBtn;

  const billedTodayFormatted = (
    <Currency value={billedToday} currency={currency} noDecimals={true} /> // $0
  );

  return (
    <div className={styles.checkoutSummary}>
      <Card className={styles.card}>
        <Inset className={styles.overviewSection}>
          <h4 className={styles.overviewTitle}>
            {title()}
          </h4>
          <div className={styles.overviewDescription}>{renderOverview()}</div>
        </Inset>

        {shouldShowBillingOptions && (
          <Inset>
            <DurationSelectSideBySide dataTestId="duration-select-side-by-side" />
          </Inset>
        )}

        <Inset className={styles.pricingSection}>
          <div
            className={clsx(styles.lineItem, styles.billedToday)}
          >
            <div className={styles.priceRow} data-testid="footer_billed_today">
              <span>{I18n.t(`${I18nPrefixTrial}.billed.today`)}</span>
              <span>{billedTodayFormatted}</span>
            </div>
          </div>

          <div
            className={clsx(styles.lineItem, styles.billedAfterTrial)}
            data-testid="billed-after-trial"
          >
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefixTrial}.billed.after_trial`)}</span>
              <span>{totalPrimaryText}</span>
            </div>
          </div>
        </Inset>

        <Inset className={styles.optionsSection} dataTestId="options-section">
          <div className={styles.moreBillingOptions}>
            {shouldShowBillingOptionsBtn && (
              <button
                className={clsx(
                  'btn',
                  'btn-link',
                  styles.moreBillingOptionsButton
                )}
                type="button"
                data-testid="show-more-billing-options"
                onClick={handleBillingOptionsClick}
              >
                {moreBillingOptionsButtonText}
              </button>
            )}
            {shouldShowDivider && (
              <span className={styles.actionDivider}>·</span>
            )}
            {shouldShowPromoCodeInput && (
              <PromoCodeInput />
            )}
            {shouldShowPromoCodeInputBtn && (
              <button
                className={clsx(
                  'btn',
                  'btn-link',
                  styles.moreBillingOptionsButton
                )}
                onClick={handlePromoClick}
                type="button"
              >
                {I18n.t(`${I18nPrefixTrial}.redeem_promo_code`)}
              </button>
            )}
          </div>
        </Inset>
      </Card>
    </div>
  );
};

export default SummaryTrials;
